import type { Property } from "@/types/property";

/**
 * One rule for "how many rooms does this listing have", shared by the detail
 * page, the share text, the SEO title/description and the listing cards.
 *
 * Land and commercial records are frequently saved with a numeric `0` for
 * bedroom/bathroom counts (the create form defaults to `0`). Treating `0` or a
 * missing value as "has zero rooms" is what produced share messages like
 * "(0 beds, 0 baths)". A count is only reported when it is positive *and*
 * meaningful for the category.
 */

type Category = "RESIDENTIAL" | "COMMERCIAL" | "INDUSTRIAL" | "LAND" | "SPECIAL_PURPOSE";

const CATEGORIES: Category[] = [
  "RESIDENTIAL",
  "COMMERCIAL",
  "INDUSTRIAL",
  "LAND",
  "SPECIAL_PURPOSE",
];

function normalizeCategory(value?: string | null): Category | null {
  if (!value) return null;
  const upper = value.trim().toUpperCase();
  return (CATEGORIES as string[]).includes(upper) ? (upper as Category) : null;
}

/** Legacy records predate categories and only carry a property_type. */
function looksLikeLand(property: Property): boolean {
  return /\b(land|plot|plots|acre|acreage)\b/i.test(property.property_type ?? "");
}

export function isLandProperty(property: Property): boolean {
  const category = normalizeCategory(property.property_category);
  if (category) return category === "LAND";
  return looksLikeLand(property);
}

export function isResidentialProperty(property: Property): boolean {
  const category = normalizeCategory(property.property_category);
  if (category) return category === "RESIDENTIAL";
  return !looksLikeLand(property);
}

function positiveInt(value?: number | null): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  const rounded = Math.floor(value);
  return rounded > 0 ? rounded : null;
}

export interface RoomCounts {
  bedrooms: number | null;
  bathrooms: number | null;
  toilets: number | null;
  plots: number | null;
}

/**
 * Room/footprint counts for display. `null` means "say nothing" — never print
 * it as zero. Bedrooms are residential-only; bathrooms and toilets also make
 * sense for commercial/industrial buildings; plots are land-only.
 */
export function roomCounts(property: Property): RoomCounts {
  const land = isLandProperty(property);
  const residential = isResidentialProperty(property);

  return {
    bedrooms: residential ? positiveInt(property.bedroom_count) : null,
    bathrooms: land ? null : positiveInt(property.bathroom_count),
    toilets: land ? null : positiveInt(property.toilet),
    plots: land ? positiveInt(property.number_of_plots) : null,
  };
}

/** Land footprint, e.g. "500 sqm". */
export function landSizeLabel(property: Property): string | null {
  const sqm = positiveInt(property.total_land_area_sqm);
  if (!sqm) return null;
  return `${sqm.toLocaleString("en-US")} sqm`;
}

/** "3 beds, 2 baths" / "2 plots" / "" — never contains a zero count. */
export function roomSummary(property: Property): string {
  const { bedrooms, bathrooms, plots } = roomCounts(property);
  const parts: string[] = [];
  if (bedrooms) parts.push(`${bedrooms} bed${bedrooms === 1 ? "" : "s"}`);
  if (bathrooms) parts.push(`${bathrooms} bath${bathrooms === 1 ? "" : "s"}`);
  if (plots) parts.push(`${plots} plot${plots === 1 ? "" : "s"}`);
  return parts.join(", ");
}
